import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/styles';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3CB0DB]/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'border border-primary-500/20 bg-primary-500 text-white shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px active:shadow-sm',
        secondary:
          'border border-white/30 bg-white/50 text-neutral-800 shadow-sm hover:bg-white/60 hover:shadow-md active:translate-y-px',
        outline:
          'border border-neutral-300/50 bg-transparent text-neutral-700 hover:bg-white/30 hover:border-neutral-400/50 active:translate-y-px',
        danger:
          'border border-red-500/20 bg-red-500 text-white shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px',
        success:
          'border border-green-600/20 bg-forest-500 text-white shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px',
        ghost: 'border border-transparent text-neutral-600 hover:bg-white/20 hover:border-white/20',
        link: 'text-primary-500 underline-offset-4 hover:underline font-semibold',
        glass:
          'bg-white/25 backdrop-blur-[20px] border-[0.5px] border-white/25 text-neutral-800 hover:bg-white/40 active:bg-white/50',
        'glass-primary':
          'bg-primary-500/80 backdrop-blur-[20px] border-[0.5px] border-white/25 text-white hover:bg-primary-500/90 active:bg-primary-500',
      },
      size: {
        sm: 'px-4 py-2.5 text-sm',
        md: 'px-5 py-3.5 text-base',
        lg: 'px-7 py-4 text-lg',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
